import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  Paperclip,
  Send,
  MessageCircle,
  FileText,
  Mic,
  MicOff,
  Loader2,
  Volume2,
  VolumeX,
  Download,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  Copy,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Users,
  Calendar,
  Phone,
  BarChart3,
  Search,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import apiService from "../../services/api";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { Bubble } from "iconsax-reactjs";

const AskGenziButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [copiedMessageId, setCopiedMessageId] = useState(null);

  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthesisRef = useRef(window.speechSynthesis);
  const audioContextRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const messageInputRef = useRef(null);

  const quickActions = [
    {
      id: "summary",
      text: "Today's meetings",
      icon: <Calendar size={16} />,
      color: "purple",
    },
    {
      id: "calls",
      text: "Recent calls",
      icon: <Phone size={16} />,
      color: "blue",
    },
    {
      id: "team",
      text: "Team performance",
      icon: <Users size={16} />,
      color: "green",
    },
    {
      id: "insights",
      text: "Key insights",
      icon: <TrendingUp size={16} />,
      color: "orange",
    },
    {
      id: "action",
      text: "My action items",
      icon: <CheckCircle size={16} />,
      color: "red",
    },
    {
      id: "analytics",
      text: "Call analytics",
      icon: <BarChart3 size={16} />,
      color: "indigo",
    },
    {
      id: "search",
      text: "Search transcripts",
      icon: <Search size={16} />,
      color: "pink",
    },
    {
      id: "schedule",
      text: "Upcoming meetings",
      icon: <Clock size={16} />,
      color: "teal",
    },
  ];

  // Initialize speech recognition with enhanced settings
  useEffect(() => {
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;

      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = "en-US";
      recognitionRef.current.maxAlternatives = 3;

      let finalTranscript = "";
      let interimTranscript = "";

      recognitionRef.current.onresult = (event) => {
        interimTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;

          if (event.results[i].isFinal) {
            finalTranscript += transcript + " ";
            setMessage(finalTranscript.trim());
          } else {
            interimTranscript += transcript;
            setMessage(finalTranscript + interimTranscript);
          }
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
        setIsRecording(false);

        if (event.error === "no-speech") {
          // Auto restart if no speech detected
          setTimeout(() => {
            if (isRecording) {
              recognitionRef.current.start();
            }
          }, 1000);
        }
      };

      recognitionRef.current.onend = () => {
        if (isRecording) {
          // Auto restart for continuous listening
          try {
            recognitionRef.current.start();
          } catch (e) {
            console.log("Recognition restart failed:", e);
          }
        } else {
          setIsListening(false);
          setIsRecording(false);
        }
      };

      recognitionRef.current.onspeechend = () => {
        if (message.trim()) {
          // Auto-send after speech ends
          setTimeout(() => {
            if (!isListening) {
              handleSendMessage();
            }
          }, 1500);
        }
      };
    }

    // Initialize Audio Context for advanced audio processing
    audioContextRef.current = new (
      window.AudioContext || window.webkitAudioContext
    )();

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [isRecording]);
  useEffect(() => {
    if (isOpen && !sessionId) {
      const newSessionId = `${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      setSessionId(newSessionId);
      setConversationId(newSessionId);
    }
  }, [isOpen]);
  // Load suggestions on mount
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadSuggestions();
    }
  }, [isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Generate conversation ID
  useEffect(() => {
    if (isOpen && !conversationId) {
      setConversationId(
        `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      );
    }
  }, [isOpen]);

  const loadSuggestions = async () => {
    try {
      const response = await apiService.getSuggestions({
        context: selectedOption,
        recent: true,
      });
      setSuggestions(response.suggestions || []);
    } catch (error) {
      console.error("Failed to load suggestions:", error);
    }
  };

  const handleSendMessage = async (text = message) => {
    if (!text.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: text,
      timestamp: new Date(),
      conversationId,
      sessionId,
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setIsLoading(true);
    setIsListening(false);
    setIsRecording(false);

    try {
      // Stop any ongoing speech recognition
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }

      let fullResponse = null;
      let streamedContent = "";

      await apiService.askGenziStream(
        text,
        (chunk) => {
          try {
            const data = JSON.parse(chunk);

            if (data.type === "chunk") {
              streamedContent += data.content;
            } else if (data.type === "complete") {
              fullResponse = data.response;

              // Update sessionId if server returns a new one
              if (data.response.metadata?.sessionId) {
                setSessionId(data.response.metadata.sessionId);
              }
            }

            // Update UI with streaming content
            if (streamedContent && !fullResponse) {
              setMessages((prev) => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];

                if (
                  lastMessage &&
                  lastMessage.type === "assistant" &&
                  lastMessage.isStreaming
                ) {
                  lastMessage.content = JSON.parse(streamedContent);
                } else {
                  newMessages.push({
                    id: Date.now() + 1,
                    type: "assistant",
                    content: {
                      type: "streaming",
                      data: { text: streamedContent },
                    },
                    timestamp: new Date(),
                    isStreaming: true,
                    sessionId,
                  });
                }

                return newMessages;
              });
            }
          } catch (e) {
            console.error("Error parsing chunk:", e);
          }
        },
        {
          sessionId: sessionId, // Always pass sessionId
          conversationContext: {
            recentMessages: messages.slice(-10),
            // currentTopics: extractCurrentTopics(),
          },
        },
      );

      // Final response
      if (fullResponse) {
        const assistantMessage = {
          id: Date.now() + 1,
          type: "assistant",
          content: fullResponse,
          timestamp: new Date(),
          conversationId,
          sessionId,
        };

        setMessages((prev) => {
          const filtered = prev.filter((m) => !m.isStreaming);
          return [...filtered, assistantMessage];
        });

        // Speak response if audio enabled
        if (audioEnabled && fullResponse.summary) {
          speakText(fullResponse.summary);
        }

        // Update suggestions based on response
        if (fullResponse.suggestions) {
          setSuggestions(fullResponse.suggestions);
        }
      }
    } catch (error) {
      console.error("Error processing message:", error);

      const errorMessage = {
        id: Date.now() + 1,
        type: "assistant",
        content: {
          type: "error",
          data: {
            text: "I encountered an issue processing your request. Please try again or rephrase your question.",
            error: error.message,
          },
        },
        timestamp: new Date(),
        sessionId,
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleVoiceInput = async () => {
    if (!recognitionRef.current) {
      alert("Voice recognition is not supported in your browser.");
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      setIsRecording(false);
    } else {
      try {
        // Request microphone permission
        await navigator.mediaDevices.getUserMedia({ audio: true });

        recognitionRef.current.start();
        setIsListening(true);
        setIsRecording(true);
        setMessage("");

        // Visual feedback
        navigator.vibrate && navigator.vibrate(50);
      } catch (error) {
        console.error("Microphone access denied:", error);
        alert("Please allow microphone access to use voice input.");
      }
    }
  };

  const speakText = (text) => {
    if (!synthesisRef.current) return;

    // Cancel any ongoing speech
    synthesisRef.current.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Select a natural voice
    const voices = synthesisRef.current.getVoices();
    const preferredVoice = voices.find(
      (voice) =>
        voice.name.includes("Google") || voice.name.includes("Natural"),
    );

    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }

    synthesisRef.current.speak(utterance);
  };

  const handleQuickAction = (action) => {
    const actionMessages = {
      summary: "Summarize today's meetings with key insights and action items",
      calls: "Show me recent calls with sentiment analysis",
      team: "Display team performance metrics for this week",
      insights: "What are the key insights from recent meetings and calls?",
      action: "Show all my pending action items organized by priority",
      analytics: "Provide detailed call analytics for this week",
      search: "Help me search for specific topics in transcripts",
      schedule: "Show my upcoming meetings for the next 7 days",
    };

    const message = actionMessages[action.id];
    if (message) {
      setSelectedOption(action.id);
      handleSendMessage(message);
    }
  };

  const handleCopyMessage = (messageContent) => {
    const textToCopy =
      typeof messageContent === "string"
        ? messageContent
        : JSON.stringify(messageContent, null, 2);

    navigator.clipboard.writeText(textToCopy);
    setCopiedMessageId(messageContent.id);

    setTimeout(() => {
      setCopiedMessageId(null);
    }, 2000);
  };

  const handleExportConversation = async () => {
    try {
      const blob = new Blob(
        [JSON.stringify({ conversationId, messages }, null, 2)],
        { type: "application/json" },
      );

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `genzi-conversation-${conversationId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const toggleMessageExpansion = (messageId) => {
    setExpandedMessages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };
  const renderInsightContent = (insight) => {
    if (typeof insight === "string") {
      return insight;
    }

    if (insight && typeof insight === "object") {
      // Handle different insight types
      if (insight.type === "top_performers" && insight.data) {
        return `Top performers: ${insight.data.map((p) => p.name).join(", ")}`;
      }

      if (insight.type === "activity_summary" && insight.data) {
        const {
          totalMeetings,
          totalCalls,
          avgMeetingDuration,
          avgCallDuration,
          totalActionItems,
        } = insight.data;
        return `${totalMeetings} meetings, ${totalCalls} calls, ${totalActionItems} action items`;
      }

      if (insight.type === "sentiment_overview" && insight.data) {
        const sentiments = Object.entries(insight.data)
          .filter(([sentiment]) => sentiment !== "unknown")
          .map(([sentiment, count]) => `${sentiment}: ${count}`)
          .join(", ");
        return `Sentiment: ${sentiments}`;
      }
      console.log("insight", insight);
      return JSON.stringify(insight.data || insight);
    }

    return "Insight data unavailable";
  };
  const renderMessage = (msg) => {
    const isExpanded = expandedMessages.has(msg.id);

    if (msg.type === "user") {
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex justify-end mb-4"
        >
          <div className="bg-purple-600 text-white rounded-lg px-4 py-2 max-w-[80%] relative group">
            <p className="text-sm">{msg.content}</p>
            <div className="text-xs text-purple-200 mt-1">
              {format(new Date(msg.timestamp), "HH:mm")}
            </div>

            <button
              onClick={() => handleCopyMessage(msg.content)}
              className="absolute -left-8 top-2 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              {copiedMessageId === msg.id ? (
                <CheckCircle size={16} className="text-green-400" />
              ) : (
                <Copy size={16} className="text-gray-400 hover:text-white" />
              )}
            </button>
          </div>
        </motion.div>
      );
    }

    const content = msg.content;

    // Error message
    if (content.type === "error") {
      return (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-4"
        >
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-red-400 mt-1" size={20} />
              <div className="flex-1">
                <p className="text-red-300 text-sm">{content.data.text}</p>
                {content.data.suggestions && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {content.data.suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSendMessage(suggestion)}
                        className="text-xs px-3 py-1 bg-red-900/30 text-red-300 rounded-lg hover:bg-red-900/50 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      );
    }

    // Meeting summary
    if (content.type === "meeting_summary") {
      return (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-4"
        >
          <div className="bg-[#25252B] rounded-lg p-4">
            {content.summary && (
              <p className="text-gray-300 mb-4 leading-relaxed">
                {content.summary}
              </p>
            )}

            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-medium flex items-center gap-2">
                <Calendar size={16} className="text-purple-400" />
                {content.data.count} Meetings • {content.data.timeframe}
              </h4>
              <button
                onClick={() => toggleMessageExpansion(msg.id)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {isExpanded ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>
            </div>

            <AnimatePresence>
              {(isExpanded || content.data.meetings.length <= 2) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="space-y-2"
                >
                  {content.data.meetings.map((meeting, idx) => (
                    <div
                      key={idx}
                      className="bg-[#1A1A1F] rounded-lg p-3 border border-gray-700"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="text-white font-medium">
                          {meeting.title}
                        </h5>
                        <span className="text-xs text-gray-400">
                          {format(new Date(meeting.time), "HH:mm")} •{" "}
                          {meeting.duration}min
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-gray-400 mb-2">
                        <span className="flex items-center gap-1">
                          <Users size={12} />
                          {meeting.participants.length} participants
                        </span>
                        {meeting.sentiment && (
                          <span
                            className={`px-2 py-0.5 rounded ${
                              meeting.sentiment.overall === "positive"
                                ? "bg-green-900/30 text-green-400"
                                : meeting.sentiment.overall === "negative"
                                  ? "bg-red-900/30 text-red-400"
                                  : "bg-gray-800 text-gray-400"
                            }`}
                          >
                            {meeting.sentiment.overall}
                          </span>
                        )}
                      </div>

                      {meeting.summary && (
                        <p className="text-sm text-gray-300 mb-2">
                          {meeting.summary}
                        </p>
                      )}

                      {meeting.keyPoints && meeting.keyPoints.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {meeting.keyPoints.map((point, pidx) => (
                            <div
                              key={pidx}
                              className="text-sm text-gray-300 flex items-start"
                            >
                              <span className="text-purple-400 mr-2">•</span>
                              {point}
                            </div>
                          ))}
                        </div>
                      )}

                      {meeting.actionItems &&
                        meeting.actionItems.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-700">
                            <p className="text-xs text-gray-400 mb-1">
                              Action Items:
                            </p>
                            {meeting.actionItems.map((item, aidx) => (
                              <div
                                key={aidx}
                                className="text-sm text-orange-400 flex items-start"
                              >
                                <CheckCircle
                                  size={12}
                                  className="mr-2 mt-0.5"
                                />
                                {item.task || item}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {content.data.statistics && (
              <div className="mt-3 pt-3 border-t border-gray-700 grid grid-cols-3 gap-2">
                <div className="text-center">
                  <div className="text-lg font-semibold text-white">
                    {content.data.statistics.total}
                  </div>
                  <div className="text-xs text-gray-400">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-white">
                    {Math.round(content.data.statistics.averageDuration)}min
                  </div>
                  <div className="text-xs text-gray-400">Avg Duration</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-white">
                    {content.data.statistics.withActionItems}
                  </div>
                  <div className="text-xs text-gray-400">With Actions</div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      );
    }

    // Call analysis
    if (content.type === "call_analysis") {
      return (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-4"
        >
          <div className="bg-[#25252B] rounded-lg p-4">
            {content.summary && (
              <p className="text-gray-300 mb-4">{content.summary}</p>
            )}
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-medium flex items-center gap-2">
                <Phone size={16} className="text-blue-400" />
                {content.data.count} Calls Analyzed
              </h4>
              <button
                onClick={() => toggleMessageExpansion(msg.id)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {isExpanded ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>
            </div>
            <AnimatePresence>
              {(isExpanded || content.data.calls.length <= 3) && (
                <motion.div className="space-y-2">
                  {content.data.calls.map((call, idx) => (
                    <div
                      key={idx}
                      className="bg-[#1A1A1F] rounded-lg p-3 border border-gray-700 flex justify-between items-center"
                    >
                      <div className="flex-1">
                        <div className="text-white font-medium">
                          {call.caller}
                        </div>
                        <div className="text-xs text-gray-400">
                          {call.phone} • {call.duration}s
                        </div>
                        {call.summary && (
                          <p className="text-xs text-gray-300 mt-1">
                            {call.summary}
                          </p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-xs text-gray-400">
                          {formatDistanceToNow(new Date(call.time), {
                            addSuffix: true,
                          })}
                        </div>
                        <div
                          className={`text-xs mt-1 px-2 py-1 rounded ${
                            call.sentiment === "positive"
                              ? "bg-green-900/30 text-green-400"
                              : call.sentiment === "negative"
                                ? "bg-red-900/30 text-red-400"
                                : "bg-gray-800 text-gray-400"
                          }`}
                        >
                          {call.sentiment}
                        </div>
                        {call.escalationRequired && (
                          <div className="text-xs mt-1 px-2 py-1 rounded bg-orange-900/30 text-orange-400">
                            Escalation
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
            {content.data.insights && (
              <div className="mt-3 p-3 bg-[#1A1A1F] rounded-lg border border-gray-700">
                <h5 className="text-sm font-medium text-white mb-2">
                  Insights
                </h5>
                <div className="space-y-1">
                  {content.data.insights &&
                    Object.entries(content.data.insights).map(
                      ([key, value]) => {
                        // Skip rendering if value is an object (like sentimentBreakdown)
                        if (typeof value === "object" && value !== null) {
                          return null;
                        }

                        return (
                          <div
                            key={key}
                            className="flex justify-between text-xs"
                          >
                            <span className="text-gray-400 capitalize">
                              {key.replace(/_/g, " ")}:
                            </span>
                            <span className="text-gray-300">{value}</span>
                          </div>
                        );
                      },
                    )}
                </div>
              </div>
            )}
            {content.data.insights?.sentimentBreakdown && (
              <div className="mt-2 p-2 bg-[#1A1A1F] rounded border border-gray-700">
                <div className="text-xs text-gray-400 mb-1">
                  Sentiment Breakdown:
                </div>
                <div className="flex justify-between text-xs">
                  {Object.entries(content.data.insights.sentimentBreakdown)
                    .filter(([sentiment]) => sentiment !== "unknown")
                    .map(([sentiment, count]) => (
                      <div key={sentiment} className="text-center">
                        <div
                          className={`font-medium ${
                            sentiment === "positive"
                              ? "text-green-400"
                              : sentiment === "Negative"
                                ? "text-red-400"
                                : "text-gray-400"
                          }`}
                        >
                          {count}
                        </div>
                        <div className="text-gray-500 capitalize">
                          {sentiment}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
            {content.data.trends && content.data.trends.length > 0 && (
              <div className="mt-3 p-3 bg-[#1A1A1F] rounded-lg border border-gray-700">
                <h5 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                  <TrendingUp size={14} className="text-green-400" />
                  Trends
                </h5>
                <div className="space-y-1">
                  {content.data.trends.map((trend, idx) => (
                    <div key={idx} className="text-xs text-gray-300">
                      • {trend}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      );
    }

    // Action items
    if (content.type === "action_items") {
      return (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-4"
        >
          <div className="bg-[#25252B] rounded-lg p-4">
            {content.summary && (
              <p className="text-gray-300 mb-4">{content.summary}</p>
            )}

            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-medium flex items-center gap-2">
                <CheckCircle size={16} className="text-orange-400" />
                {content.data.total} Action Items
              </h4>
              <button
                onClick={() => toggleMessageExpansion(msg.id)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {isExpanded ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>
            </div>

            <AnimatePresence>
              {(isExpanded || content.data.items.length <= 3) && (
                <motion.div>
                  {content.data.byPriority && (
                    <div className="space-y-3">
                      {Object.entries(content.data.byPriority).map(
                        ([priority, items]) => (
                          <div key={priority}>
                            <div
                              className={`text-xs font-medium mb-1 ${
                                priority === "high"
                                  ? "text-red-400"
                                  : priority === "medium"
                                    ? "text-yellow-400"
                                    : "text-gray-400"
                              }`}
                            >
                              {priority.toUpperCase()} PRIORITY ({items.length})
                            </div>
                            {items.map((item, idx) => (
                              <div
                                key={idx}
                                className="bg-[#1A1A1F] rounded-lg p-3 mb-2 border border-gray-700"
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="text-white text-sm">
                                      {item.task}
                                    </div>
                                    {item.assignee && (
                                      <div className="text-xs text-gray-400 mt-1">
                                        Assigned to: {item.assignee}
                                      </div>
                                    )}
                                    {item.sourceTitle && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        From: {item.sourceTitle}
                                      </div>
                                    )}
                                  </div>
                                  {item.dueDate && (
                                    <div className="text-xs text-gray-400 text-right">
                                      Due:{" "}
                                      {format(new Date(item.dueDate), "MMM d")}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      );
    }

    if (content.type === "team_performance") {
      return (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-4"
        >
          <div className="bg-[#25252B] rounded-lg p-4">
            {content.summary && (
              <p className="text-gray-300 mb-4">{content.summary}</p>
            )}

            <h4 className="text-white font-medium flex items-center gap-2 mb-3">
              <Users size={16} className="text-green-400" />
              Team Performance • {content.data.timeframe}
            </h4>

            <div className="grid grid-cols-2 gap-3">
              {content.data.team && (
                <div className="bg-[#1A1A1F] rounded-lg p-3 border border-gray-700">
                  <div className="text-2xl font-bold text-white">
                    {content.data.team.length}
                  </div>
                  <div className="text-xs text-gray-400">Team Members</div>
                </div>
              )}

              {content.data.meetings && (
                <div className="bg-[#1A1A1F] rounded-lg p-3 border border-gray-700">
                  <div className="text-2xl font-bold text-white">
                    {content.data.meetings.total || 0}
                  </div>
                  <div className="text-xs text-gray-400">Meetings</div>
                </div>
              )}

              {content.data.calls && (
                <div className="bg-[#1A1A1F] rounded-lg p-3 border border-gray-700">
                  <div className="text-2xl font-bold text-white">
                    {content.data.calls.total || 0}
                  </div>
                  <div className="text-xs text-gray-400">Calls</div>
                </div>
              )}

              {content.data.sentimentBreakdown && (
                <div className="bg-[#1A1A1F] rounded-lg p-3 border border-gray-700">
                  <div className="text-2xl font-bold text-white">
                    {(() => {
                      // Calculate average sentiment from breakdown
                      const breakdown = content.data.sentimentBreakdown;
                      const total = Object.values(breakdown).reduce(
                        (sum, count) => sum + count,
                        0,
                      );
                      if (total === 0) return "N/A";

                      // Simple calculation: positive = 1, neutral = 0, negative = -1
                      const score =
                        ((breakdown.positive || 0) -
                          (breakdown.negative || 0)) /
                        total;
                      return score > 0.3
                        ? "Positive"
                        : score < -0.3
                          ? "Negative"
                          : "Neutral";
                    })()}
                  </div>
                  <div className="text-xs text-gray-400">Avg Sentiment</div>
                </div>
              )}
            </div>

            {/* Fixed insights rendering */}
            {content.data.insights && content.data.insights.length > 0 && (
              <div className="mt-3 p-3 bg-[#1A1A1F] rounded-lg border border-gray-700">
                <h5 className="text-sm font-medium text-white mb-2">
                  Key Insights
                </h5>
                <ul className="space-y-1">
                  {content.data.insights.map((insight, idx) => (
                    <li key={idx} className="text-xs text-gray-300">
                      • {renderInsightContent(insight)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </motion.div>
      );
    }

    // Search results
    if (content.type === "search_results") {
      return (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-4"
        >
          <div className="bg-[#25252B] rounded-lg p-4">
            <h4 className="text-white font-medium flex items-center gap-2 mb-3">
              <Search size={16} className="text-pink-400" />
              Found {content.data.totalResults} results for "
              {content.data.query}"
            </h4>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {content.data.results.map((result, idx) => (
                <div
                  key={idx}
                  className="bg-[#1A1A1F] rounded-lg p-3 border border-gray-700"
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          result.type === "meeting"
                            ? "bg-purple-900/30 text-purple-400"
                            : "bg-blue-900/30 text-blue-400"
                        }`}
                      >
                        {result.type}
                      </span>
                      <span className="text-xs text-gray-400">
                        {result.speaker}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(result.date), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                  <p
                    className="text-sm text-gray-300 mt-2"
                    dangerouslySetInnerHTML={{
                      __html: result.text.replace(
                        /\*\*(.*?)\*\*/g,
                        '<mark class="bg-yellow-500/30 text-yellow-300">$1</mark>',
                      ),
                    }}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {result.title}
                  </div>
                </div>
              ))}
            </div>

            {content.data.facets && (
              <div className="mt-3 pt-3 border-t border-gray-700">
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(content.data.facets.byType).map(
                    ([type, count]) => (
                      <span
                        key={type}
                        className="text-xs px-2 py-1 bg-[#1A1A1F] text-gray-400 rounded"
                      >
                        {type}: {count}
                      </span>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      );
    }

    // Default text response
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="mb-4"
      >
        <div className="bg-[#25252B] rounded-lg p-4 relative group">
          <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">
            {content.summary || content.data?.text || "Processing..."}
          </p>

          {content.metadata && (
            <div className="flex gap-3 mt-3 text-xs text-gray-500">
              {content.metadata.dataPoints && (
                <span>{content.metadata.dataPoints} data points</span>
              )}
              {content.metadata.processingTime && (
                <span>{content.metadata.processingTime}ms</span>
              )}
            </div>
          )}

          {(content.suggestions || suggestions.length > 0) && (
            <div className="flex gap-2 mt-4 flex-wrap">
              {(content.suggestions || suggestions)
                .slice(0, 4)
                .map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSendMessage(suggestion)}
                    className="text-xs px-3 py-1.5 bg-[#1A1A1F] text-gray-300 rounded-lg hover:bg-gray-700 transition-all hover:scale-105 border border-gray-700"
                  >
                    {suggestion}
                  </button>
                ))}
            </div>
          )}

          <button
            onClick={() => handleCopyMessage(content)}
            className="absolute -left-8 top-2 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {copiedMessageId === msg.id ? (
              <CheckCircle size={16} className="text-green-400" />
            ) : (
              <Copy size={16} className="text-gray-400 hover:text-white" />
            )}
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 px-6 py-3 rounded-full flex items-center justify-center text-white text-sm font-medium transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
          border: "1px solid",
          borderImageSource:
            "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
          boxShadow: `
            0px 36px 32px -16px #7F56D936,
            0px 8px 16px 0px #7F56D91F,
            0px 2px 4px -8px #7F56D914,
            0px 8px 24px -16px #FFFFFF3D inset,
            0px -24px 32px 0px #FFFFFF38 inset,
            0px 0px 12px 0px #FFFFFF inset
          `,
          borderImageSlice: "1",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background =
            "radial-gradient(50% 50% at 50% 50%, #8462D3 0%, #6647AB 100%)";
          e.currentTarget.style.boxShadow = `
            0px 42px 36px -12px #8D5EE680,
            0px 12px 20px 0px #7F56D93D,
            0px 4px 8px -4px #7F56D928,
            0px 12px 28px -12px #7F56D9 inset,
            0px -28px 36px 0px #FFFFFF4D inset,
            0px 0px 16px 0px #FFFFFF inset
          `;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background =
            "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)";
          e.currentTarget.style.boxShadow = `
            0px 36px 32px -16px #7F56D936,
            0px 8px 16px 0px #7F56D91F,
            0px 2px 4px -8px #7F56D914,
            0px 8px 24px -16px #FFFFFF3D inset,
            0px -24px 32px 0px #FFFFFF38 inset,
            0px 0px 12px 0px #FFFFFF inset
          `;
        }}
      >
        <Bubble size={17} className="mr-2" />
        Ask Octo
      </motion.button>

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 backdrop-blur-sm"
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed bottom-24 right-6 z-50 w-full max-w-md"
            >
              <div
                className="bg-[#1A1A1F] rounded-2xl shadow-2xl overflow-hidden"
                style={{
                  border: "3px solid",
                  borderImageSource:
                    "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                  boxShadow: `
      0px 36px 32px -16px #7F56D936,
      0px 8px 16px 0px #7F56D91F,
      0px 2px 4px -8px #7F56D914,
      0px 8px 24px -16px #FFFFFF3D inset,
      0px -24px 32px 0px #FFFFFF38 inset,
      0px 0px 12px 0px #FFFFFF inset
    `,
                  borderImageSlice: "1",
                  transition: "all 0.3s ease",
                }}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gradient-to-r from-purple-900/20 to-blue-900/20">
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <MessageCircle size={20} className="text-purple-400" />
                      <span className="absolute -bottom-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    </div>
                    <h3 className="text-white font-medium">
                      Octo AI Assistant
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setAudioEnabled(!audioEnabled)}
                      className={`p-2 rounded-lg transition-colors ${
                        audioEnabled
                          ? "bg-purple-600 text-white"
                          : "text-gray-400 hover:text-white hover:bg-gray-800"
                      }`}
                      title={
                        audioEnabled
                          ? "Disable voice responses"
                          : "Enable voice responses"
                      }
                    >
                      {audioEnabled ? (
                        <Volume2 size={16} />
                      ) : (
                        <VolumeX size={16} />
                      )}
                    </button>
                    <button
                      onClick={handleExportConversation}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                      title="Export conversation"
                    >
                      <Download size={16} />
                    </button>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {/* Messages Container */}
                <div className="h-[28rem] overflow-y-auto p-4 scroll-smooth horizontal-scroll-container">
                  {messages.length === 0 ? (
                    <div className="space-y-4">
                      <div className="text-gray-400 text-center py-8">
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 200 }}
                        >
                          <MessageCircle
                            size={48}
                            className="mx-auto mb-4 text-gray-600"
                          />
                        </motion.div>
                        <p className="text-sm mb-2 text-white">
                          Hi! I'm Octo, your intelligent AI assistant.
                        </p>
                        <p className="text-xs text-gray-500">
                          I can analyze meetings, calls, team performance, and
                          much more.
                        </p>
                      </div>

                      {/* Quick Actions Grid */}
                      <div className="grid grid-cols-2 gap-2">
                        {quickActions.map((action, idx) => (
                          <motion.button
                            key={action.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            onClick={() => handleQuickAction(action)}
                            className={`bg-[#25252B] hover:bg-[#2a2a32] text-gray-300 text-xs rounded-lg p-3 text-left transition-all hover:scale-105 border border-gray-700 group`}
                          >
                            <div
                              className={`text-${action.color}-400 mb-1 group-hover:scale-110 transition-transform`}
                            >
                              {action.icon}
                            </div>
                            {action.text}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div>
                      {messages.map((msg) => (
                        <div key={msg.id}>{renderMessage(msg)}</div>
                      ))}
                      {isLoading && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="flex items-center gap-2 text-gray-400 mb-4"
                        >
                          <Loader2 size={16} className="animate-spin" />
                          <span className="text-sm">
                            Analyzing your request...
                          </span>
                        </motion.div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                {/* Input Section */}
                <div className="p-4 border-t border-gray-700 bg-[#25252B]/50">
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      onClick={toggleVoiceInput}
                      className={`p-2.5 rounded-lg transition-all ${
                        isRecording
                          ? "bg-red-600 text-white animate-pulse scale-110"
                          : "text-gray-400 hover:text-white hover:bg-gray-800"
                      }`}
                      title={
                        isRecording ? "Stop recording" : "Start voice input"
                      }
                    >
                      {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
                    </button>

                    <div className="flex-1 relative">
                      <input
                        ref={messageInputRef}
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={
                          isListening
                            ? "Listening... Speak clearly"
                            : "Ask me anything..."
                        }
                        className="w-full bg-[#1A1A1F] text-white placeholder-gray-500 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none  border border-gray-700 transition-all"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        disabled={isListening}
                      />
                      {message && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                          {message.length}
                        </span>
                      )}
                    </div>

                    <button
                      onClick={() => handleSendMessage()}
                      disabled={!message.trim() || isLoading}
                      className="p-2.5 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
                      style={{
                        background:
                          "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                        border: "1px solid",
                        borderImageSource:
                          "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                        boxShadow: `
            0px 36px 32px -16px #7F56D936,
            0px 8px 16px 0px #7F56D91F,
            0px 2px 4px -8px #7F56D914,
            0px 8px 24px -16px #FFFFFF3D inset,
            0px -24px 32px 0px #FFFFFF38 inset,
            0px 0px 12px 0px #FFFFFF inset
          `,
                        borderImageSlice: "1",
                      }}
                    >
                      {isLoading ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Send size={18} />
                      )}
                    </button>
                  </div>
                  {/* 
                  {suggestions.length > 0 && messages.length > 0 && (
                    <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                      {suggestions.slice(0, 3).map((suggestion, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSendMessage(suggestion)}
                          className="text-xs px-3 py-1 bg-[#1A1A1F] text-gray-400 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap border border-gray-700"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )} */}

                  <div className="text-xs text-gray-500 text-center mt-2">
                    Powered by Octo AI •{" "}
                    {isRecording ? "Voice mode active" : "Type or speak"}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default AskGenziButton;
